import {
    BaseEntity,
    BaseExplorerConfig,
    EntityServiceAdapter,
    FieldTransformer,
    QueryParams,
    QueryResult,
    ExplorerFeatureConfig,
    PresentationConfig
} from '../../../types/baseExplorerConfig';
import { WeatherStationEntityEntry } from '../../../domain/model/weatherStation.js';
import { faTowerBroadcast } from '@fortawesome/free-solid-svg-icons';
import {
    WeatherStationQueryParams,
    WeatherStationResponse,
    CreateWeatherStationRequest,
    UpdateWeatherStationRequest
} from '../../../types/weatherStation';
import { WeatherStationService } from '../../../services/weatherStationService';
import { objectToSearchParams } from '../baseExplorer/objectToSearchParams';
import { backendPage } from '../../../utils/pagination';
import { STATION_COLUMNS } from '../../../shared/stationSchema';
import { gridColumns } from '../../../shared/columnSchema';

// Initialize the service
const weatherStationService = new WeatherStationService();

// Field Transformers
const nesdisTransformer: FieldTransformer = {
    transform: (value: any) => value ? String(value).toUpperCase() : 'N/A',
    reverseTransform: (value: any) => String(value || ''),
    validationRules: [
        {
            type: 'required',
            message: 'NESDIS ID is required'
        },
        {
            type: 'pattern',
            value: /^[A-Z0-9]+$/i,
            message: 'NESDIS ID must contain only letters and numbers'
        }
    ]
};

const nameTransformer: FieldTransformer = {
    transform: (value: any) => value ? String(value).trim() : 'Unnamed Station',
    reverseTransform: (value: any) => String(value || ''),
    validationRules: [
        {
            type: 'required',
            message: 'Station name is required'
        },
        {
            type: 'minLength',
            value: 2,
            message: 'Station name must be at least 2 characters'
        }
    ]
};

const regionTransformer: FieldTransformer = {
    transform: (value: any) => {
        const regionMap: Record<string, string> = {
            'SS': 'South Slave',
            'EV': 'Beaufort Delta',
            'FS': 'Dehcho',
            'ZF': 'North Slave',
            'VQ': 'Sahtu',
            'WBNP': 'Wood Buffalo National Park'
        };
        return regionMap[value] || value || 'Unknown';
    },
    reverseTransform: (value: any) => String(value || ''),
    validationRules: [
        {
            type: 'required',
            message: 'Region is required'
        }
    ]
};

const statusTransformer: FieldTransformer = {
    transform: (value: any) => {
        const statusMap: Record<string, string> = {
            'Fully Operational': '🟢 Operational',
            'Demobilized': '🔴 Demobilized',
            'Maintenance': '🟡 Maintenance'
        };
        return statusMap[value] || value || 'Unknown';
    },
    reverseTransform: (value: any) => String(value || ''),
    validationRules: [
        {
            type: 'required',
            message: 'Status is required'
        }
    ]
};

const elevationMetersTransformer: FieldTransformer = {
    transform: (value: any) => {
        if (!value && value !== 0) return 'N/A';
        return `${Number(value).toFixed(0)}m`;
    },
    reverseTransform: (value: any) => Number(value || 0),
    validationRules: [
        {
            type: 'range',
            value: { min: -500, max: 5000 },
            message: 'Elevation must be between -500m and 5000m'
        }
    ]
};

const geometryTransformer: FieldTransformer = {
    transform: (value: any) => {
        if (!value?.coordinates) return 'No coordinates';
        const [lon, lat] = value.coordinates;
        return `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
    },
    reverseTransform: (value: any) => value,
    validationRules: [
        {
            type: 'required',
            message: 'Coordinates are required'
        }
    ]
};

const cffdrsModeTransformer: FieldTransformer = {
    transform: (value: any) => {
        const modeMap: Record<string, string> = {
            'off': '⚫ Off',
            'spring': '🌱 Spring',
            'summer': '☀️ Summer'
        };
        return modeMap[value] || value || 'Not set';
    },
    reverseTransform: (value: any) => String(value || '')
};

const contactTransformer: FieldTransformer = {
    transform: (value: any) => value || 'No contact',
    reverseTransform: (value: any) => String(value || ''),
    validationRules: [
        {
            type: 'pattern',
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: 'Contact must be a valid email address'
        }
    ]
};

// Region is stored as a 2-letter code (e.g. "ZF") but rendered as the
// full label (e.g. "North Slave") via regionTransformer. When a user
// searches the displayed value we translate label → code so the backend's
// `region ILIKE` clause can match.
const REGION_LABEL_TO_CODE: Record<string, string> = {
    'south slave': 'SS',
    'beaufort delta': 'EV',
    'dehcho': 'FS',
    'north slave': 'ZF',
    'sahtu': 'VQ',
    'wood buffalo national park': 'WBNP',
};

const PASS_THROUGH_FIELDS = new Set([
    'region', 'status', 'cffdrsMode', 'nesdis', 'name', 'contact',
]);

const FILTER_FIELD_MAP: Record<string, string> = {
    elevationMeters: 'elevation',
};

/**
 * Translate CBE filter/search/sort params into the flat shape the weather
 * station backend expects. Single source of truth used by both fetchData
 * (view) and buildExportParams (export).
 */
function translateParams(params: QueryParams): Record<string, any> {
    const out: Record<string, any> = {};

    let searchTerm = params?.search?.term;
    if (searchTerm) {
        const matchedCode = REGION_LABEL_TO_CODE[searchTerm.toLowerCase().trim()];
        if (matchedCode) searchTerm = matchedCode;
        out.search = searchTerm;
    }

    if (params?.sort?.[0]) {
        out.sortBy = params.sort[0].field;
        out.sortOrder = params.sort[0].direction;
    }

    if (!params?.filters) return out;

    for (const filter of params.filters) {
        const mappedKey = FILTER_FIELD_MAP[filter.field];
        if (mappedKey) {
            out[mappedKey] = filter.value;
            continue;
        }
        if (PASS_THROUGH_FIELDS.has(filter.field)) {
            out[filter.field] = filter.value;
            continue;
        }
        console.warn(`WSE ServiceAdapter: Unsupported filter field: ${filter.field}`);
    }

    return out;
}

// Service Adapter
export const weatherStationServiceAdapter: EntityServiceAdapter<WeatherStationEntityEntry> = {
    async fetchData(params: QueryParams): Promise<QueryResult<WeatherStationEntityEntry>> {
        const queryParams: WeatherStationQueryParams = {
            ...backendPage(params),
            ...translateParams(params),
        };

        const response: WeatherStationResponse = await weatherStationService.fetchData(queryParams);

        return {
            data: response.data,
            total: response.total,
            page: response.page,
            limit: response.limit,
            hasMore: response.page < response.totalPages
        };
    },

    buildExportParams: (params: QueryParams) => objectToSearchParams(translateParams(params)),

    async createEntity(data: Omit<WeatherStationEntityEntry, '_id'>): Promise<WeatherStationEntityEntry> {
        const createRequest: CreateWeatherStationRequest = {
            nesdis: data.nesdis!,
            name: data.name!,
            region: data.region!,
            geometry: data.geometry!,
            wkt: data.wkt!,
            elevationMeters: data.elevationMeters,
            status: data.status!,
            xmitTime: data.xmitTime,
            contact: data.contact,
            sensorSuite: data.sensorSuite,
            sensorTokens: data.sensorTokens,
            sensorArray: data.sensorArray,
            cffdrsMode: data.cffdrsMode,
            cffdrsMeta: data.cffdrsMeta,
            fuelType: data.fuelType
        };

        return await weatherStationService.createStation(createRequest);
    },

    async updateEntity(id: string, data: Partial<WeatherStationEntityEntry>): Promise<WeatherStationEntityEntry> {
        const updateRequest: UpdateWeatherStationRequest = {
            nesdis: data.nesdis,
            name: data.name,
            region: data.region,
            geometry: data.geometry,
            wkt: data.wkt,
            elevationMeters: data.elevationMeters,
            status: data.status,
            xmitTime: data.xmitTime,
            contact: data.contact,
            sensorSuite: data.sensorSuite,
            sensorTokens: typeof data.sensorTokens === 'string' ? [data.sensorTokens] : data.sensorTokens,
            sensorArray: data.sensorArray,
            cffdrsMode: data.cffdrsMode,
            cffdrsMeta: data.cffdrsMeta,
            fuelType: data.fuelType
        };

        return await weatherStationService.updateStation(id, updateRequest);
    },

    async deleteEntity(id: string): Promise<boolean> {
        await weatherStationService.deleteStation(id);
        return true;
    }
};

// Feature Configuration
const features: ExplorerFeatureConfig = {
    pagination: {
        enabled: true,
        defaultPageSize: 25,
        pageSizeOptions: [25, 50, 100, 200],
        showPageInfo: true
    },
    sorting: {
        enabled: true,
        multiColumn: false,
        defaultSort: [{ field: 'name', direction: 'asc' }]
    },
    filtering: {
        enabled: true,
        quickFilters: [
            {
                field: 'region',
                label: 'Region',
                type: 'dropdown',
                options: [
                    { value: 'SS', label: 'South Slave' },
                    { value: 'EV', label: 'Beaufort Delta' },
                    { value: 'FS', label: 'Dehcho' },
                    { value: 'ZF', label: 'North Slave' },
                    { value: 'VQ', label: 'Sahtu' },
                    { value: 'WBNP', label: 'Wood Buffalo National Park' }
                ]
            },
            {
                field: 'status',
                label: 'Status',
                type: 'dropdown',
                options: [
                    { value: 'Fully Operational', label: '🟢 Fully Operational' },
                    { value: 'Demobilized', label: '🔴 Demobilized' },
                    { value: 'Maintenance', label: '🟡 Maintenance' }
                ]
            },
            {
                field: 'cffdrsMode',
                label: 'CFFDRS Mode',
                type: 'dropdown',
                options: [
                    { value: 'off', label: '⚫ Off' },
                    { value: 'spring', label: '🌱 Spring' },
                    { value: 'summer', label: '☀️ Summer' }
                ]
            },
            { field: 'nesdis', label: 'NESDIS ID', type: 'textSearch' },
            { field: 'name', label: 'Station Name', type: 'textSearch' },
            { field: 'geometry', label: 'Coordinates', type: 'textSearch' },
            { field: 'elevationMeters', label: 'Elevation', type: 'textSearch' },
            { field: 'contact', label: 'Contact', type: 'textSearch' }
        ],
        advancedFilters: true
    },
    searching: {
        enabled: true,
        placeholder: 'Search stations by name, ID, region, or contact...',
        searchFields: ['name', 'nesdis', 'region', 'contact'],
        debounceMs: 300
    },
    editing: {
        enabled: true,
        mode: 'modal',
        allowCreate: true,
        allowUpdate: true,
        allowDelete: true
    },
    export: {
        enabled: true,
        formats: ['csv', 'excel'],
        includeFilters: true,
        entity: 'stations'
    }
};

// Presentation Configuration
const presentation: PresentationConfig = {
    title: 'Weather Stations',
    subtitle: 'Manage and monitor weather station network',
    icon: faTowerBroadcast,

    columnConfig: gridColumns(STATION_COLUMNS),
    layout: {
        minHeight: 400,
        responsive: true,
        showToolbar: true,
        showFooter: true
    }
};

// Complete Configuration
export const cleanStationConfig: BaseExplorerConfig<WeatherStationEntityEntry> = {
    entityName: 'Weather Station',
    complexityLevel: 'moderate',
    displayFields: ['nesdis', 'name', 'region', 'status', 'geometry', 'elevationMeters', 'contact'],
    fieldTransformers: {
        nesdis: nesdisTransformer,
        name: nameTransformer,
        region: regionTransformer,
        status: statusTransformer,
        elevationMeters: elevationMetersTransformer,
        geometry: geometryTransformer,
        cffdrsMode: cffdrsModeTransformer,
        contact: contactTransformer
    },
    serviceAdapter: weatherStationServiceAdapter,
    features,
    presentation
};